import React, { useState } from "react";
import { useFilterStore } from "@/stores/useFilterStore";
import { DropdownSelector } from "@/components/dropdown/DropDown";
import CommonButton from "@/components/common/button/CommonButton";
import { THEME } from "@/constants";
import { JOB_CODE, ORDER_BY, PICK_UP_DELIVERY, PROJECT_NAME, STATUS, TEXT_APPLY, TEXT_CLEAR, TEXT_FILTERS } from "@/constants/strings";
import { FilterKey } from "@/types/SitesSketches";

export const MobileFilterModal: React.FC<{
  handleFilterData: (filterType: string, data?: any) => void;
  setSearchInputValue: (value: string) => void;
  setOptions: (key: string, options: any[]) => void;
  onClose: () => void;
  onClear: () => void;
}> = ({ handleFilterData, setSearchInputValue, onClose, onClear }) => {
  const filters = useFilterStore(state => state.filters);
  const selected = useFilterStore(state => state.selected);
  const setSelected = useFilterStore(state => state.setSelected);
  const clearFilter = useFilterStore(state => state.clearFilter);

  const [selectedFilters, setSelectedFilters] = useState<Record<FilterKey, string[]>>(() => {
    const initial: Record<FilterKey, string[]> = {
      projectNames: [],
      orderBy: [],
      jobCodes: [],
      statuses: [],
      states: []
    };
    (Object.keys(initial) as FilterKey[]).forEach(key => {
      initial[key] = (selected[key] || []).map(opt => opt.id);
    });
    return initial;
  });

  const handleSelect = (key: FilterKey, ids: string[]) => {
    setSelectedFilters(prev => ({
      ...prev,
      [key]: ids
    }));
  };

  const handleClear = () => {
    clearFilter();
    setSearchInputValue("");
    const resetState: Record<FilterKey, string[]> = {
      projectNames: [],
      orderBy: [],
      jobCodes: [],
      statuses: [],
      states: []
    };
    (Object.keys(resetState) as FilterKey[]).forEach(key => {
      resetState[key] = (filters[key] || []).map((opt: any) => opt.id);
    });
    setSelectedFilters(resetState);
    onClear();
    onClose();
  };

  const handleApply = () => {
    (Object.entries(selectedFilters) as [FilterKey, string[]][]).forEach(([key, ids]) => {
      const options = filters[key] || [];
      let selectedOptions = options.filter(option => ids.includes(option.id));
      if (selectedOptions.length === 0) {
        selectedOptions = options;
      }
      setSelected(key, selectedOptions);
    });

    handleFilterData("filter", selectedFilters);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/25 flex justify-center items-center z-50">
      <div className="bg-white w-[90%] max-w-[400px] rounded-lg p-4 relative">
        <button className="absolute top-3 right-3 text-gray-500 text-xl close" onClick={onClose}>
          &times;
        </button>

        <h2 className="text-lg font-semibold mb-4">{TEXT_FILTERS}</h2>

        <div className="flex flex-col gap-3 mb-4">
          <DropdownSelector
            title={PROJECT_NAME}
            options={filters.projectNames}
            selected={selectedFilters.projectNames}
            onSelect={ids => handleSelect("projectNames", ids)}
          />
          <DropdownSelector
            title={ORDER_BY}
            options={filters.orderBy}
            selected={selectedFilters.orderBy}
            onSelect={ids => handleSelect("orderBy", ids)}
          />
          <DropdownSelector
            title={JOB_CODE}
            options={filters.jobCodes}
            selected={selectedFilters.jobCodes}
            onSelect={ids => handleSelect("jobCodes", ids)}
          />
          <DropdownSelector
            title={STATUS}
            options={filters.statuses}
            selected={selectedFilters.statuses}
            onSelect={ids => handleSelect("statuses", ids)}
            canSearch={false}
          />
          <DropdownSelector
            title={PICK_UP_DELIVERY}
            options={filters.states}
            selected={selectedFilters.states}
            onSelect={ids => handleSelect("states", ids)}
            canSearch={false}
          />
        </div>

        <div className="flex justify-between">
          <CommonButton
            label={TEXT_CLEAR}
            backgroundColor={THEME.COLORS.WHITE}
            className="text-gray-900 rounded-md border border-gray-300 w-[45%] h-10"
            onClick={handleClear}
          />
          <CommonButton
            label={TEXT_APPLY}
            backgroundColor={THEME.COLORS.PRIMARY}
            className="text-white rounded-md w-[45%] h-10"
            onClick={handleApply}
          />
        </div>
      </div>
    </div>
  );
};
